import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { Formik, Form, FormikHelpers, useField, useFormikContext } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormikControl } from '../formik';
import { MarketingConsentMsg } from '../shared';
import { useCloudampDataRef } from '../../../hooks/useCloudampData';
import { useFormPromoText } from '../../../hooks/useFormPromoText';
import type { FormPromoPlacement } from '../../../utils/formPromoConfig';
import { useSelfScheduleTimer, pushDataLayerEvent, SELF_SCHEDULE_EVENTS } from '../../../hooks/useSelfScheduleTimer';
import { handleFormNavigation } from '../../../hooks/useFormPrePopulation';
import { useOnPageSelfScheduleConfig } from '../../../hooks/useOnPageSelfScheduleConfig';
import { trackSubmitLeadTrackingForm } from '../../../optimizely/optimizely';
import { callVerifyHuman } from '../../../utils';
import { INTENT, buildOnPageClientInfo, writeOnPageHandoff } from '../../../utils/selfScheduleHandoff';
import { ON_PAGE_SELF_SCHEDULE_FORM_ID } from '../../../utils/selfScheduleConfig';
import { isValidUSPhone } from '../../../utils/phone';
import {
    onPageSelfScheduleInitialValues,
    onPageSelfScheduleSchema,
    OnPageSelfScheduleFormValues,
    ON_PAGE_INTENT,
} from '.';
import {
    defaultOnPageSelfScheduleConfig,
    OnPageSelfScheduleConfig,
    SECONDARY_CTA,
    CUSTOM_QUOTE_PATH,
} from './onPageSelfScheduleConfigs';
import './OnPageSelfScheduleForm.css';

// DOM ids pending confirmation from whoever owns GTM (see WEBDEV-4043 notes) —
// defaulted to corporate's existing ConsultationForm convention since
// "corporate-schedule-btn" is already shared with it. Change here if GTM
// needs this form to match the self-schedule app's own ids instead.
// The quote button gets its own id so GTM triggers keyed on #ask-a-question
// don't count quote clicks as questions (WEBDEV-4259).
const SUBMIT_BUTTON_ID = 'corporate-schedule-btn';
const ASK_QUESTION_BUTTON_ID = 'ask-a-question';
const CUSTOM_QUOTE_BUTTON_ID = 'get-a-custom-quote';

// FullStory element names (WEBDEV-4188). Matched to the SSA's own Step 1 buttons
// (same corporate-schedule-btn id, see SelfScheduleFsElement in corporate-monorepo's
// apps/self-schedule/lib/fullstory.ts) so FullStory tracks the corporate on-page form
// and the SSA's Step 1 as the same CTA regardless of which surface the user is on.
const SCHEDULE_FS_ELEMENT = 'Self Schedule - Step Next CTA';
const ASK_QUESTION_FS_ELEMENT = 'Self Schedule - Ask Question CTA';
const CUSTOM_QUOTE_FS_ELEMENT = 'Self Schedule - Custom Quote CTA';

// `form_type` on this form's GTM dataLayer events.
const FORM_TYPE = 'onPageSelfSchedule';

// Own reCAPTCHA action name (distinct from ConsultationForm's "corporateConsult"
// and ContactForm's "contactForm") so verifyHuman's server-side score is scoped
// per form. Rendered inside Layout's <GoogleReCaptchaProvider>, so no separate
// provider is needed here.
const RECAPTCHA_ACTION = 'onPageSelfSchedule';

type ContactFieldName = 'first_name' | 'last_name' | 'email' | 'phone';
type FieldKind = 'name' | 'email' | 'phone';

// Live, content-only validity checks (independent of Formik's touched/error
// state) — mirrors the self-schedule app's own Step 1 (isNonEmpty/isValidEmail/
// isValidPhone), so the checkmark badge appears as soon as a field looks
// right, not only after blur/submit.
const isNonEmptyValue = (value: string) => value.trim().length > 0;
const isValidEmailValue = (value: string) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
// Same rule as phoneField() in formSchemas, so the badge and the submit check agree.
const isValidPhoneValue = (value: string) => isValidUSPhone(value);

// Same copy as the self-schedule app's Step 1 (global.validation in its i18n
// content) — "Required" when empty, a field-specific message when non-empty
// but malformed. Name fields have no malformed state (isValid === isNonEmpty).
const FIELD_VALIDATORS: Record<FieldKind, { isValid: (value: string) => boolean; invalidIndicator?: string }> = {
    name: { isValid: isNonEmptyValue },
    email: { isValid: isValidEmailValue, invalidIndicator: 'Enter Valid Email Address' },
    phone: { isValid: isValidPhoneValue, invalidIndicator: 'Enter Valid Phone Number' },
};

// Single source of truth for "is this field currently showing an error,"
// shared by each ValidatedField (to render its own error) and the parent (to
// decide whether any field has a visible error, which disables the buttons).
const hasVisibleError = (value: string, kind: FieldKind, touched: boolean, submitCount: number) =>
    !FIELD_VALIDATORS[kind].isValid(value) && (touched || submitCount > 0);

function CheckmarkBadge() {
    return (
        <span className="onpage-valid-badge" aria-hidden="true">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                    d="M10.4546 0.196237C10.5852 -0.00381994 10.8536 -0.0601943 11.0538 0.070318C11.2538 0.200889 11.3102 0.469372 11.1797 0.669491L4.40715 11.054C4.34094 11.1555 4.23508 11.2243 4.1156 11.2442C3.99593 11.264 3.87302 11.2328 3.77756 11.158L0.165661 8.32606C-0.0223441 8.17864 -0.0551874 7.90648 0.0921386 7.71844C0.239588 7.53038 0.511705 7.49746 0.699756 7.64491L3.93982 10.1853L10.4546 0.196237Z"
                    fill="white"
                />
            </svg>
        </span>
    );
}

interface ValidatedFieldProps {
    name: ContactFieldName;
    label: string;
    control: 'input' | 'emailInput' | 'phoneInput';
    type?: string;
    kind: FieldKind;
    placeholder: string;
}

// Renders a corporate FormikControl input, but with SSA-style Step-1 visuals
// layered on top: an external bold/compact label with the error indicator
// appended in parens (never a separate line under the input — matches SSA's
// ValidatedFieldLabel), and a checkmark badge once the field's own content
// looks valid. FormikControl's own label/asterisk and below-input ErrorMessage
// are suppressed (no label prop passed here; ".text-error" hidden via CSS)
// since this field owns both itself. See OnPageSelfScheduleForm.css.
function ValidatedField({ name, label, control, type, kind, placeholder }: ValidatedFieldProps) {
    const [field, meta] = useField<string>(name);
    const { submitCount } = useFormikContext();
    const { isValid: isValidValue, invalidIndicator } = FIELD_VALIDATORS[kind];

    const isValid = isValidValue(field.value);
    const showError = hasVisibleError(field.value, kind, meta.touched, submitCount);
    const errorIndicator = isNonEmptyValue(field.value) && invalidIndicator ? invalidIndicator : 'Required';

    return (
        <div className="onpage-field">
            <label htmlFor={name} className={`onpage-label ${showError ? 'onpage-label-error' : ''}`.trim()}>
                {label}
                {showError && <span className="onpage-label-error-indicator"> ({errorIndicator})</span>}
            </label>
            <div
                className={`relative ${isValid ? 'onpage-field-valid' : ''} ${showError ? 'onpage-field-invalid' : ''}`.trim()}
            >
                <FormikControl
                    control={control}
                    name={name}
                    {...(type ? { type } : {})}
                    placeholder={placeholder}
                    aria-invalid={showError}
                />
                {isValid && <CheckmarkBadge />}
            </div>
            <div role="status" className="sr-only">
                {showError ? `${label}: ${errorIndicator}` : ''}
            </div>
        </div>
    );
}

interface OnPageSelfScheduleFormProps {
    config?: OnPageSelfScheduleConfig;
    /**
     * Opts this form into the GATSBY_FORM_PROMOS line for `placement` (WEBDEV-4371),
     * which replaces the config's subtitle while a campaign is live.
     */
    promo?: { placement: FormPromoPlacement };
}

export function OnPageSelfScheduleForm({
    config: configProp = defaultOnPageSelfScheduleConfig,
    promo,
}: OnPageSelfScheduleFormProps) {
    const config = useOnPageSelfScheduleConfig(configProp);
    const promoText = useFormPromoText(promo?.placement);
    const subtitle = promoText ?? config.subtitle;
    const secondaryIntent =
        config.secondaryCta === SECONDARY_CTA.quote ? ON_PAGE_INTENT.quote : ON_PAGE_INTENT.question;
    const isQuoteCta = secondaryIntent === ON_PAGE_INTENT.quote;
    const cloudampDataRef = useCloudampDataRef();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { startTimer } = useSelfScheduleTimer();

    // Formik resets isSubmitting=false as soon as the async onSubmit resolves,
    // but writeOnPageHandoff's location.assign() doesn't block — so there's a
    // window where the page is still visible with the buttons re-enabled,
    // wide enough (reCAPTCHA's round trip) for a second click to fire another
    // handoff write before the browser navigates. isRedirecting latches the
    // buttons disabled until that navigation actually happens (same fix as
    // ConsultationForm.js's isRedirecting, WEBDEV-3553) — and the pageshow
    // handler below releases it if the visitor comes back.
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Back from /schedule or /specials/estimate usually restores this page from
    // the back/forward cache without re-mounting it, so isRedirecting stays
    // latched and both buttons are stuck on "Submitting..." (WEBDEV-4488).
    // `pageshow` with `persisted` marks that restore.
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (!event.persisted) return;
            setIsRedirecting(false);
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    // "Get A Custom Quote" (WEBDEV-4259): the same path ConsultationForm's
    // navigateToExternalForm takes on /lp/google-v2 — stash the fields under the
    // `form_data` localStorage key the estimate page's useFormPrePopulation reads,
    // then a full page load of /specials/estimate?autofill=true (cloudamp re-runs
    // there). No reCAPTCHA and no lead on this side: the quote form verifies and
    // submits on the estimate page, exactly as it does for the legacy form's
    // visitors. The only addition is the dataLayer event, awaited so the redirect
    // doesn't drop it.
    const navigateToCustomQuote = async (values: OnPageSelfScheduleFormValues) => {
        setIsRedirecting(true);
        await pushDataLayerEvent(SELF_SCHEDULE_EVENTS.CUSTOM_QUOTE, { form_type: FORM_TYPE });
        handleFormNavigation({
            navigationPath: CUSTOM_QUOTE_PATH,
            formValues: {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                phone: values.phone,
            },
            eventName:
                typeof window !== 'undefined'
                    ? `${window.location.pathname} Form to Estimate Navigation`
                    : 'Form to Estimate Navigation',
            storageKey: 'form_data',
        });
    };

    const handleSubmit = async (
        values: OnPageSelfScheduleFormValues,
        helpers: FormikHelpers<OnPageSelfScheduleFormValues>
    ) => {
        // Clears any 'Failed' banner left over from a previous bot verdict —
        // otherwise a stale message could flash while this attempt is in flight.
        helpers.setStatus(undefined);

        if (values.intent === ON_PAGE_INTENT.quote) {
            await navigateToCustomQuote(values);
            return;
        }

        // Analytics parity with ConsultationForm: fires self_schedule_start (via
        // useSelfScheduleTimer) and captures the same instant as startedAt so it
        // can ride along in the handoff payload — gated on schedule intent only,
        // matching control's own consultType === 'Self Schedule' gate. Falls back
        // to 'schedule' the same way writeOnPageHandoff does, so an Enter-key
        // submit with no button onClick still gets timed correctly.
        const effectiveIntent = values.intent || INTENT.schedule;
        let startedAt: number | undefined;
        if (effectiveIntent === INTENT.schedule) {
            startTimer({ form_type: FORM_TYPE });
            startedAt = Date.now();
        }

        // Fails open on anything other than a confirmed bot score (missing
        // recaptcha, a verifyHuman API error) — same philosophy as
        // ConsultationForm/ContactForm: don't block a real lead over a
        // verification hiccup, only over an actual bot score.
        const verification = await callVerifyHuman(RECAPTCHA_ACTION, executeRecaptcha);
        if (verification === 'bot') {
            // A real user can land here on a reCAPTCHA v3 false positive, so
            // this needs to be visible and recoverable, not a silent dead end.
            // Resetting intent avoids a stale value driving the wrong button's
            // "Submitting..." label or the wrong SSA screen on a later Enter-key
            // submit that skips the button onClick handlers.
            helpers.setStatus('Failed');
            helpers.setFieldValue('intent', '');
            helpers.setSubmitting(false);
            return;
        }

        // Same Optimizely conversion event ConsultationForm fires (its closest
        // structural match: after validation + reCAPTCHA, before lead
        // creation/handoff) — without this the on-page-form variant would
        // report a full conversion collapse on this metric regardless of
        // actual performance.
        trackSubmitLeadTrackingForm();

        const clientInfo = buildOnPageClientInfo(values);
        // Set before writeOnPageHandoff (not after): it navigates away
        // synchronously-from-here-on, whether via its normal path or its
        // storage-write-failure fallback — both end in a navigation, so
        // isRedirecting should latch either way.
        setIsRedirecting(true);
        writeOnPageHandoff(clientInfo, values.intent, { cloudampData: cloudampDataRef.current?.value, startedAt });
    };

    return (
        /* No top padding on mobile (WEBDEV-4240): the card below is full-bleed and
           light-grey there, so any section padding above it reads as a band of white
           between this form and the preceding section — the card's own pt-[0.75rem]
           supplies what little breathing room the heading needs. From md up the card
           becomes width-capped and rounded with white showing around it by design,
           so the symmetric py-12 stays. */
        <section
            id={ON_PAGE_SELF_SCHEDULE_FORM_ID}
            className="onpage-self-schedule-form position-relative pb-8 md:py-12"
        >
            {/* Both consult-form anchors — kept here for direct #consult/#googlelp-quote
                deep-links, not for CTA clicks: useSelfScheduleCtaRedirect intercepts
                those and sends them straight to /schedule by design (confirmed
                intentional — this form is for organic scrollers, not CTA traffic). */}
            <span id="consult" className="anchor-tag"></span>
            <span id="googlelp-quote" className="anchor-tag"></span>
            {/* Only the top tightens (WEBDEV-4240 is scoped to the gap above the
                form) — pb-8 keeps the CTA row off the card's bottom edge, and md:p-8
                restores a symmetric 2rem from the breakpoint up.

                pt-[0.75rem] rather than pt-3: bootstrap.min.css is imported after
                @tailwind utilities (utils/styles/global.css) and Tailwind runs with
                important:false, so Bootstrap's `.p{t,y}-3{padding:1rem!important}`
                would beat Tailwind's 0.75rem — and, not being media-scoped, would
                override md:p-8's vertical padding too, silently halving the card's
                desktop padding. Same reason every other unprefixed spacing value
                here is an arbitrary value (px-[18px], mt-[1rem], gap-[0.5rem]);
                pb-8 is safe because Bootstrap's scale stops at 5. */}
            <div className="w-full bg-[#f8fafb] px-[18px] pb-8 pt-[0.75rem] md:mx-auto md:max-w-xl md:rounded-2xl md:p-8 lg:max-w-2xl">
                <h2 className="font-brandon text-2xl font-bold text-milan-navy md:text-3xl">{config.title}</h2>
                {/* text-sm (14px) matches the self-schedule app's own Step 1 subheadline —
                    without it this <p> inherits the site-wide 18px `p` rule
                    (utils/styles/global.css), which is also what was pushing the
                    gap to the first field wider than the SSA's. */}
                {subtitle && <p className="mt-[0.25rem] text-sm text-milan-md-grey">{subtitle}</p>}

                <Formik
                    initialValues={onPageSelfScheduleInitialValues}
                    validationSchema={onPageSelfScheduleSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={false}
                >
                    {(formik) => {
                        // Enabled by default (unlike the self-schedule app's own Step 1,
                        // which disables until the whole form is valid) — but once a field
                        // is actually showing an error (touched/submitted-and-invalid), stop
                        // the user from proceeding until it's fixed.
                        const anyFieldInvalid =
                            hasVisibleError(
                                formik.values.first_name,
                                'name',
                                !!formik.touched.first_name,
                                formik.submitCount
                            ) ||
                            hasVisibleError(
                                formik.values.last_name,
                                'name',
                                !!formik.touched.last_name,
                                formik.submitCount
                            ) ||
                            hasVisibleError(formik.values.email, 'email', !!formik.touched.email, formik.submitCount) ||
                            hasVisibleError(formik.values.phone, 'phone', !!formik.touched.phone, formik.submitCount);

                        return (
                            <Form className="fs-mask mt-[1rem]" method="POST">
                                <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                <div className="flex gap-[0.5rem] md:gap-3">
                                    <div className="basis-1/2">
                                        <ValidatedField
                                            name="first_name"
                                            label="First Name"
                                            control="input"
                                            type="text"
                                            kind="name"
                                            placeholder="First Name"
                                        />
                                    </div>
                                    <div className="basis-1/2">
                                        <ValidatedField
                                            name="last_name"
                                            label="Last Name"
                                            control="input"
                                            type="text"
                                            kind="name"
                                            placeholder="Last Name"
                                        />
                                    </div>
                                </div>

                                <div className="mt-[0.75rem]">
                                    <ValidatedField
                                        name="email"
                                        label="Email Address"
                                        control="emailInput"
                                        kind="email"
                                        placeholder="you@email.com"
                                    />
                                </div>

                                <div className="mt-[0.75rem]">
                                    <ValidatedField
                                        name="phone"
                                        label="Phone Number"
                                        control="phoneInput"
                                        type="tel"
                                        kind="phone"
                                        placeholder="+1 999-999-9999"
                                    />
                                </div>

                                <div className="onpage-legal-text mt-[1rem]">
                                    <MarketingConsentMsg
                                        cta={
                                            <>
                                                {config.buttonLabels.inquiry}
                                                <sup>*</sup> or {config.buttonLabels.schedule}
                                                <sup>**</sup>
                                            </>
                                        }
                                    />
                                </div>

                                {formik.status === 'Failed' && (
                                    <p className="onpage-label-error mt-[1rem] text-sm" role="alert">
                                        Something went wrong. Please try again or{' '}
                                        <Link to="/company/contact-us/" className="underline">
                                            contact us
                                        </Link>
                                        .
                                    </p>
                                )}

                                {/* "See Available Times" is first in the DOM (and this row is
                                    flex-row-reverse'd back to its visual order) so it's the
                                    form's default button — pressing Enter in any field submits
                                    via this button, not "Ask A Question". Same trick
                                    ConsultationForm uses (flex-sm-row-reverse). */}
                                <div className="onpage-cta-row mt-[0.75rem] md:mt-8 flex flex-row-reverse gap-1.5 -mx-3 md:mx-0">
                                    <button
                                        id={SUBMIT_BUTTON_ID}
                                        type="submit"
                                        className="onpage-btn onpage-btn-primary basis-1/2"
                                        disabled={anyFieldInvalid || formik.isSubmitting || isRedirecting}
                                        onClick={() => formik.setFieldValue('intent', INTENT.schedule)}
                                        data-fs-element={SCHEDULE_FS_ELEMENT}
                                    >
                                        {(formik.isSubmitting || isRedirecting) &&
                                        formik.values.intent === INTENT.schedule ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                {config.buttonLabels.schedule}
                                                <sup>**</sup>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        id={isQuoteCta ? CUSTOM_QUOTE_BUTTON_ID : ASK_QUESTION_BUTTON_ID}
                                        type="submit"
                                        className="onpage-btn onpage-btn-secondary basis-1/2"
                                        disabled={anyFieldInvalid || formik.isSubmitting || isRedirecting}
                                        onClick={() => formik.setFieldValue('intent', secondaryIntent)}
                                        data-fs-element={isQuoteCta ? CUSTOM_QUOTE_FS_ELEMENT : ASK_QUESTION_FS_ELEMENT}
                                    >
                                        {(formik.isSubmitting || isRedirecting) &&
                                        formik.values.intent === secondaryIntent ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                {config.buttonLabels.inquiry}
                                                <sup>*</sup>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </section>
    );
}

export default OnPageSelfScheduleForm;
